import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  Injector,
  input,
  output,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatDialogRef } from '@angular/material/dialog';
import { RouterLink } from '@angular/router';
import { CdkTrapFocus } from '@angular/cdk/a11y';
import { InterventionPhotos } from '../intervention-photos/intervention-photos';
import { ReferencePhotosIntervention } from '../../Services/intervention-photos';
import {
  ConsultationIntervention,
  InterventionApi,
  preparerEditionIntervention,
} from '../../Services/intervention-api';
import { messageErreurApi } from '../../Services/api-error';
import { Authentification } from '../../Services/authentification';
import { InterventionAcces } from '../intervention-acces/intervention-acces';
import { InterventionSuppression } from '../intervention-suppression/intervention-suppression';
import type { NatureIntervention } from '../../Services/intervention-edition';

export type InterventionModalInformation = {
  label: string;
  value: string;
  icon?: string;
};

export type InterventionModalData = {
  /** Nature connue par la liste d’origine, pour refuser une action sur un type contradictoire. */
  nature?: NatureIntervention;
  eyebrow?: string;
  title: string;
  informations: InterventionModalInformation[];
  detailsLabel: string;
  details: string;
  ticketLink?: string[] | null;
  ticketLabel?: string;
  referencePhotos?: ReferencePhotosIntervention;
};

@Component({
  selector: 'app-intervention-modal',
  imports: [
    RouterLink,
    CdkTrapFocus,
    InterventionPhotos,
    InterventionAcces,
    InterventionSuppression,
  ],
  templateUrl: './intervention-modal.html',
  styleUrl: './intervention-modal.css',
  host: {
    '(document:keydown.escape)': 'fermerSiDisponible()',
    '[style.display]': 'editionEnCours() ? "none" : null',
  },
})
export class InterventionModalComponent {
  readonly data = input.required<InterventionModalData>();
  readonly fermer = output<void>();
  private readonly api = inject(InterventionApi);
  private readonly auth = inject(Authentification);
  private readonly destroyRef = inject(DestroyRef);
  private readonly injector = inject(Injector);
  private readonly element = inject<ElementRef<HTMLElement>>(ElementRef);
  private readonly revision = signal(0);
  private boutonEdition: HTMLElement | null = null;
  readonly consultation = signal<ConsultationIntervention | null>(null);
  readonly chargement = signal(false);
  readonly erreur = signal('');
  readonly editionEnCours = signal(false);
  readonly planificationEditable = computed(() => {
    const planification = this.consultation()?.planification;
    return planification?.permissions.modifier ? preparerEditionIntervention(planification) : null;
  });

  /** Une lecture fournit les photos et les actions ; les anciennes réponses restent consultables. */
  constructor() {
    this.auth.sessionChanges.pipe(takeUntilDestroyed()).subscribe(() => this.fermer.emit());
    effect((onCleanup) => {
      const reference = this.data().referencePhotos;
      const session = this.auth.sessionVersion;
      this.revision();
      let actif = true;
      onCleanup(() => {
        actif = false;
      });
      const estActif = () => actif && session === this.auth.sessionVersion;
      this.consultation.set(null);
      this.erreur.set('');
      this.chargement.set(!!reference);
      if (!reference) return;
      void this.api
        .consulter(reference.id, reference.categorie)
        .then(
          (consultation) => {
            if (!estActif()) return;
            if (
              this.data().nature === 'planification' &&
              consultation.suppression?.nature === 'bon'
            ) {
              this.erreur.set(
                'Le serveur renvoie un bon pour cette planification. Son classement doit être corrigé avant de la modifier ou de la supprimer.',
              );
              return;
            }
            this.consultation.set(consultation);
          },
          (erreur: unknown) => {
            if (estActif())
              this.erreur.set(
                messageErreurApi(erreur, 'Impossible de charger les détails de l’intervention.'),
              );
          },
        )
        .finally(() => {
          if (estActif()) this.chargement.set(false);
        });
    });
  }

  /** Relit les détails après un échec sans conserver les anciennes actions. */
  reessayer(): void {
    this.revision.update((revision) => revision + 1);
  }

  /** Un seul dialogue est visible et capte le clavier ; l’annulation rend le focus à la consultation. */
  suivreEdition(reference: Pick<MatDialogRef<unknown>, 'afterClosed'>): void {
    const actif = this.element.nativeElement.ownerDocument.activeElement;
    this.boutonEdition =
      actif instanceof HTMLElement && this.element.nativeElement.contains(actif) ? actif : null;
    this.editionEnCours.set(true);
    reference
      .afterClosed()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((resultat) => {
        this.editionEnCours.set(false);
        if (resultat) this.fermer.emit();
        else
          afterNextRender(
            () => {
              (
                this.boutonEdition ??
                this.element.nativeElement.querySelector<HTMLElement>(
                  'app-intervention-acces button, app-intervention-suppression button',
                )
              )?.focus();
            },
            { injector: this.injector },
          );
      });
  }

  fermerSiDisponible(): void {
    if (!this.editionEnCours()) this.fermer.emit();
  }

  fermerSurBackdrop(event: MouseEvent) {
    if (event.target === event.currentTarget) {
      this.fermerSiDisponible();
    }
  }
}
